import Box from '@mui/material/Box';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOpenXAgentContext } from '../../../context/useOpenXAgentContext.ts';
import { OpenXAgentFilters } from '../shared/OpenXAgentFilters.tsx';
import { OpenXAgentPanelHeader } from '../shared/OpenXAgentPanelHeader.tsx';
import { OpenXAgentPanelSection } from '../shared/OpenXAgentPanelSection.tsx';
import { OpenXAgentStatTiles } from '../shared/OpenXAgentStatTiles.tsx';
import { panelRootSx } from '../shared/utils/styles.ts';
import { useItemSelection } from '../shared/utils/useItemSelection.ts';
import { MatchedDomainAppTable } from './components/MatchedDomainAppTable.tsx';
import { MatchedDomainAppToolbar } from './components/MatchedDomainAppToolbar.tsx';
import { DealType } from './utils/consts.ts';
import type { OpenXAgentMatchedDomainApp, OpenXAgentMatchedDomainAppPanelData } from './utils/types.ts';

type OpenXAgentMatchedDomainAppPanelProps = OpenXAgentMatchedDomainAppPanelData & {
  onBack?: () => void;
  onEditFilters?: () => void;
  onExport?: (selectedItems: OpenXAgentMatchedDomainApp[]) => void;
};

export const OpenXAgentMatchedDomainAppPanel = ({
  title,
  filters,
  stats,
  items,
  onBack,
  onEditFilters,
  onExport,
}: OpenXAgentMatchedDomainAppPanelProps) => {
  const { t } = useTranslation();
  const { closeSidePanel } = useOpenXAgentContext();
  const [searchQuery, setSearchQuery] = useState('');
  const [dealType, setDealType] = useState<DealType>(DealType.DIRECT);

  const visibleItems = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return query ? items.filter(({ name }) => name.toLowerCase().includes(query)) : items;
  }, [items, searchQuery]);

  const { selectedIds, selectedItems, toggleItem, selectAllVisible, deselectAllVisible } = useItemSelection(
    items,
    visibleItems
  );

  const handleExport = useCallback(() => onExport?.(selectedItems), [onExport, selectedItems]);

  return (
    <Box data-test="openx-agent-matched-domain-app-panel" sx={panelRootSx}>
      <OpenXAgentPanelHeader title={title} onBack={onBack ?? closeSidePanel} onExport={handleExport} />

      <OpenXAgentFilters filters={filters} onEditFilters={onEditFilters} />

      <OpenXAgentStatTiles stats={stats} maxColumns={4} />

      <OpenXAgentPanelSection title={`${t('Matched Domains / App Bundles')} [ ${items.length} ]`}>
        <MatchedDomainAppToolbar
          searchQuery={searchQuery}
          onSearchQueryChange={setSearchQuery}
          dealType={dealType}
          onDealTypeChange={setDealType}
        />

        <MatchedDomainAppTable
          items={visibleItems}
          selectedIds={selectedIds}
          onToggleItem={toggleItem}
          onSelectAll={selectAllVisible}
          onDeselectAll={deselectAllVisible}
        />
      </OpenXAgentPanelSection>
    </Box>
  );
};
